import { useEffect, useMemo, useState } from "react";
import { PhoneCall, Sparkles } from "lucide-react";
import { T, FONT_MONO } from "@/lib/hybridTheme";

/**
 * Animated phone-call explainer that plays a 4-turn transcript in a phone-
 * frame with a live waveform underneath. Loops indefinitely so the visitor
 * can pick it up mid-scroll and understand the product in ~6 seconds.
 *
 * The transcript is driven by the `sample_call` field returned by
 * /api/public/industries/{key}, so admins can edit it per-vertical.
 */
export default function CallExplainer({ industryLabel, transcript, businessName }) {
  const script = useMemo(() => (Array.isArray(transcript) && transcript.length ? transcript : [
    { s: "caller", t: "Hi, I'm calling to ask about your services." },
    { s: "ai",     t: "Happy to help — what are you looking for?" },
    { s: "caller", t: "Just some info and pricing." },
    { s: "ai",     t: "I can text you a quote and book a follow-up. Best number?" },
  ]), [transcript]);

  const [turnIdx, setTurnIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [phase, setPhase] = useState("typing"); // typing → holding → next

  useEffect(() => {
    let timer;
    const current = script[turnIdx] || script[0];
    const full = current.t;
    if (phase === "typing") {
      if (charIdx < full.length) {
        timer = setTimeout(() => setCharIdx((c) => c + 1), 24 + Math.random() * 12);
      } else {
        setPhase("holding");
        timer = setTimeout(() => setPhase("next"), 1100);
      }
    } else if (phase === "next") {
      const nextIdx = (turnIdx + 1) % script.length;
      setTurnIdx(nextIdx);
      setCharIdx(0);
      setPhase("typing");
    }
    return () => clearTimeout(timer);
  }, [turnIdx, charIdx, phase, script]);

  const shown = script.slice(0, turnIdx + 1).map((turn, i) =>
    i === turnIdx ? { ...turn, t: turn.t.slice(0, charIdx) } : turn
  );

  const displayName = businessName || `${industryLabel || "Local"} pros`;

  return (
    <div
      data-testid="call-explainer"
      style={{
        background: "linear-gradient(180deg, rgba(20,27,45,0.7) 0%, rgba(10,14,26,0.9) 100%)",
        border: `1px solid ${T.line}`,
        padding: "24px 20px 22px",
        maxWidth: 420,
        boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Coral corner glow */}
      <div style={{
        position: "absolute", top: -60, right: -60, width: 200, height: 200,
        background: `radial-gradient(circle, ${T.coral} 0%, transparent 70%)`,
        opacity: 0.15, pointerEvents: "none",
      }} />

      {/* Phone frame header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, position: "relative" }}>
        <div style={{
          width: 42, height: 42, borderRadius: 42, background: T.coral,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 0 0 4px rgba(229,83,59,0.15)",
        }}>
          <PhoneCall size={18} color="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(245,247,250,0.5)" }}>
            Incoming call · answered in 0.8s
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.ink, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {displayName}
          </div>
        </div>
        <div style={{
          fontFamily: FONT_MONO, fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
          color: "#7EE787", display: "inline-flex", alignItems: "center", gap: 6,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: 6, background: "#7EE787", boxShadow: "0 0 8px #7EE787" }} />
          Live
        </div>
      </div>

      {/* Waveform */}
      <div style={{ display: "flex", alignItems: "flex-end", height: 34, gap: 2, marginBottom: 18 }}>
        {Array.from({ length: 38 }).map((_, i) => (
          <span
            key={i}
            style={{
              flex: 1,
              background: `linear-gradient(180deg, ${T.coral} 0%, rgba(229,83,59,0.35) 100%)`,
              height: "40%",
              animation: `ce-wave 1.2s ease-in-out ${(i * 0.05).toFixed(2)}s infinite alternate`,
              transformOrigin: "bottom",
            }}
          />
        ))}
      </div>

      {/* Transcript */}
      <div style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.06)",
        padding: 16,
        minHeight: 200,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }} data-testid="call-explainer-transcript">
        {shown.map((turn, i) => (
          <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: turn.s === "ai" ? "flex-start" : "flex-end" }}>
            <div style={{
              fontFamily: FONT_MONO, fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase",
              color: turn.s === "ai" ? T.coral : "rgba(245,247,250,0.4)",
              marginBottom: 2,
            }}>
              {turn.s === "ai" ? "AI receptionist" : "Caller"}
            </div>
            <div style={{
              fontSize: 13, lineHeight: 1.45, color: T.ink,
              background: turn.s === "ai" ? "rgba(229,83,59,0.12)" : "rgba(255,255,255,0.06)",
              border: turn.s === "ai" ? "1px solid rgba(229,83,59,0.25)" : "1px solid rgba(255,255,255,0.08)",
              padding: "8px 12px",
              maxWidth: "88%",
              whiteSpace: "pre-wrap",
            }}>
              {turn.t}
              {i === shown.length - 1 && phase === "typing" && (
                <span style={{ display: "inline-block", width: 6, height: 12, background: T.coral, marginLeft: 3, verticalAlign: "-1px", animation: "ce-blink 0.8s steps(2) infinite" }} />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer stat */}
      <div style={{
        marginTop: 14, display: "flex", alignItems: "center", gap: 8,
        fontFamily: FONT_MONO, fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
        color: "rgba(245,247,250,0.55)",
      }}>
        <Sparkles size={11} />
        Books, quotes, and texts back — automatically.
      </div>

      <style>{`
        @keyframes ce-wave  { 0% { height: 15%; } 100% { height: 92%; } }
        @keyframes ce-blink { 0% { opacity: 1; } 50% { opacity: 0; } 100% { opacity: 1; } }
      `}</style>
    </div>
  );
}
